import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { useTranslation } from "react-i18next";

import { InfoBox } from "@/components/InfoBox";
import { BarLoading } from "@/components/Loading/BarLoading";
import { getApiErrorBodyFromUnknownError } from "@/features/error/handleError";
import { interlude } from "@/interlude";
import { ChannelQueriesContext } from "@/pages/channels/pages/detail/channelQueriesContext";
import { IceBreakersSection } from "@/pages/channels/pages/detail/whatsapp/IceBreakersSection";

const styles = {
  errorBox: {
    maxWidth: "520px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

export const Automations: React.FC = function Automations() {
  const { t } = useTranslation();
  const { channel } = ChannelQueriesContext.useData();
  // Deliberately not the Suspense variant: this GET proxies Meta's Graph API,
  // and a Meta-side failure (expired channel token, inaccessible object) comes
  // back as a 502. A suspense query would throw that to the nearest boundary,
  // which for this page is the router's `errorComponent` on
  // `channels/$type/$id` -- i.e. the whole detail page is replaced by the
  // channel list, with no trace of what went wrong. Handling the state here
  // keeps the failure inside the tab panel.
  const conversationalComponentsQuery =
    interlude.channel.useGetConversationalComponents(
      {
        params: {
          channelId: channel.id,
        },
      },
      // 502 is a 5xx, so the shared retry policy would retry it: several
      // seconds of `BarLoading` before the same Meta error surfaces anyway.
      // Meta rejecting the call is not transient -- fail fast instead.
      { retry: false },
    );

  if (channel.type !== "whatsapp") return null;

  if (conversationalComponentsQuery.isPending) return <BarLoading />;

  if (conversationalComponentsQuery.isError) {
    // The API's `message` carries Meta's raw Graph API text (e.g. "Meta Graph
    // API error 190: The token has expired on ..."), which is what actually
    // tells support/ops which token or object is broken -- the mapped generic
    // copy alone would not. It is empty for the synthesized network / no-token
    // errors; those get the generic copy on its own rather than sitting under
    // an "external service" title that would claim more than we know.
    const metaErrorMessage = getApiErrorBodyFromUnknownError(
      conversationalComponentsQuery.error,
    )?.message;

    return (
      <InfoBox
        variant="error"
        title={
          metaErrorMessage ? t("common.apiError.externalServiceError") : null
        }
        description={
          metaErrorMessage || t("common.applicationError.unknownError")
        }
        sx={styles.errorBox}
      />
    );
  }

  return (
    <IceBreakersSection
      channelId={channel.id}
      channelName={channel.name}
      phoneNumber={channel.channelInformation.phoneNumber}
      initialPrompts={conversationalComponentsQuery.data.prompts}
    />
  );
};
